// ============================================================
// funcionalidades/secciones.jsx — las secciones de la pantalla de venta,
// que el vendedor arma como quiera.
//
// Antes estaban escritas en el código: Maquillaje, Uñas, Brochas... Eso
// sirve para un puesto de cosméticos y para ningún otro. Un puesto de
// ropa, uno de dulces o una papelería necesitan las suyas.
//
// Aquí se pueden agregar, renombrar, cambiarles la figura y el color,
// moverlas de lugar y borrarlas. Al borrar una que tiene producto, hay
// que decir a dónde se va ese producto: nada se queda huérfano.
//
// Las figuras son emoji, no imágenes. A propósito: no pesan, no se bajan
// de ningún lado, se ven iguales en todos los teléfonos y no hay que
// pedirle permiso a nadie. Quien quiera foto de verdad en su sección,
// puede tomarla con la cámara (el mismo recorte de fondo del producto).
// ============================================================

import React, { useState } from "react";
import { LayoutGrid, Plus, Trash2, ChevronUp, ChevronDown, Check, X, Pencil, Camera } from "lucide-react";
import { COL, tarjeta, inputStyle, labelStyle } from "./tema.jsx";
import { bitacora } from "./bitacora.jsx";
import { TomarFoto } from "./fondo.jsx";
import { nuevoId } from "./ids.jsx";

// Con las que arranca un puesto de cosméticos. Cualquiera las puede cambiar.
export const SECCIONES_BASE = [
  { id: "maq", nombre: "Maquillaje", emoji: "💄", color: "#C96B8E" },
  { id: "unas", nombre: "Uñas", emoji: "💅", color: "#D4699B" },
  { id: "broch", nombre: "Brochas", emoji: "🖌️", color: "#B08968" },
  { id: "perf", nombre: "Perfumes", emoji: "🧴", color: "#8E7BC9" },
  { id: "piel", nombre: "Cuidado de piel", emoji: "🧼", color: "#5EA88A" },
  { id: "temp", nombre: "Temporada", emoji: "🎃", color: "#E0803C" },
  { id: "acc", nombre: "Accesorios", emoji: "👛", color: "#7BA6C9" },
];

// El menú de figuras. Por familias, porque buscar entre cien emoji
// sueltos no es buscar: es rendirse.
export const FIGURAS = [
  { familia: "Belleza", lista: ["💄", "💅", "🖌️", "🧴", "🧼", "💍", "👑", "🪮", "🧽", "🪞", "🎀", "✨"] },
  { familia: "Ropa", lista: ["👕", "👖", "👗", "👚", "🧦", "👟", "👠", "🧢", "🧥", "👜", "🩳", "🧣"] },
  { familia: "Niños", lista: ["🧸", "🚗", "🪀", "🎈", "🧩", "⚽", "🪁", "🎨", "🍼", "🪆", "🎮", "🛹"] },
  { familia: "Comida", lista: ["🍬", "🍫", "🥤", "🍪", "🌮", "🍞", "🥫", "🧂", "🍎", "🥚", "☕", "🍿"] },
  { familia: "Papelería", lista: ["✏️", "📒", "📏", "✂️", "🖊️", "📎", "🖍️", "📐", "🗂️", "📌", "🎒", "🏷️"] },
  { familia: "Casa", lista: ["🧹", "🪣", "🧻", "🍽️", "🪥", "🕯️", "🔌", "💡", "🧺", "🪑", "🛏️", "🚿"] },
  { familia: "Herramienta", lista: ["🔧", "🔨", "🪛", "🪚", "🔩", "🧰", "📦", "🔋", "🧯", "⛓️", "🪜", "🧤"] },
  { familia: "Fiesta", lista: ["🎃", "🎄", "🎁", "🎉", "🧁", "💐", "🕶️", "🎺", "🎊", "❤️", "🪅", "🎇"] },
  { familia: "Otras", lista: ["📱", "🎧", "⌚", "🔑", "💊", "🌿", "🐶", "🐱", "⭐", "🔥", "🏷️", "🛒"] },
];

export const COLORES = ["#C96B8E", "#D4699B", "#B08968", "#8E7BC9", "#5EA88A",
  "#E0803C", "#7BA6C9", "#C4462E", "#2F7D53", "#B8862B", "#6B7A8F", "#9C5B8A"];

const idNuevo = () => nuevoId();

// ------------------------------------------------------------
// La pantalla. Lista arriba, y al tocar una se abre su ficha.
// ------------------------------------------------------------
export default function Secciones({ secciones, setSecciones, productos, setProductos, onCerrar }) {
  const [edita, setEdita] = useState(null);     // sección en edición, o {nueva:true}
  const [borrar, setBorrar] = useState(null);   // sección que se quiere quitar

  const cuantos = (id) => productos.filter((p) => p.cat === id).length;

  const mover = (i, hacia) => {
    const j = i + hacia;
    if (j < 0 || j >= secciones.length) return;
    const l = [...secciones];
    [l[i], l[j]] = [l[j], l[i]];
    setSecciones(l);
    bitacora.paso("secciones", "sección movida de lugar", { id: l[j].id, de: i, a: j });
  };

  const guardar = (s) => {
    if (s.nueva) {
      const nueva = { id: idNuevo(), nombre: s.nombre.trim(), emoji: s.emoji, color: s.color, foto: s.foto || null };
      setSecciones([...secciones, nueva]);
      bitacora.paso("secciones", "sección agregada", { nombre: nueva.nombre });
    } else {
      setSecciones(secciones.map((x) => (x.id === s.id
        ? { ...x, nombre: s.nombre.trim(), emoji: s.emoji, color: s.color, foto: s.foto || null } : x)));
      bitacora.paso("secciones", "sección cambiada", { id: s.id });
    }
    setEdita(null);
  };

  // Quitar una sección NUNCA debe perder producto. Si tiene, se mueve.
  const quitar = (sec, destino) => {
    const n = cuantos(sec.id);
    if (n > 0) {
      if (!destino) return;
      setProductos((ps) => ps.map((p) => (p.cat === sec.id ? { ...p, cat: destino } : p)));
    }
    setSecciones(secciones.filter((x) => x.id !== sec.id));
    bitacora.paso("secciones", "sección quitada", { id: sec.id, movidos: n, destino: destino || null });
    setBorrar(null);
  };

  if (edita) return <FichaSeccion sec={edita} onCancel={() => setEdita(null)} onOk={guardar} />;

  return (
    <div style={{ padding: "12px 14px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <div style={{ fontSize: 20, fontWeight: 800, flex: 1 }}>Tus secciones</div>
        {onCerrar && (
          <button onClick={onCerrar} aria-label="Cerrar"
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}>
            <X size={22} color={COL.kraft} />
          </button>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: COL.kraft, fontWeight: 600, marginBottom: 13, lineHeight: 1.4 }}>
        Así se ve la pantalla de venta. Acomódalas como tengas el puesto: lo que más vendes, primero.
      </div>

      {secciones.map((s, i) => (
        <div key={s.id} style={{ ...tarjeta, padding: 11, marginBottom: 8, display: "flex",
          alignItems: "center", gap: 11, borderLeft: `5px solid ${s.color}` }}>
          <div style={{ width: 44, height: 44, borderRadius: 11, background: COL.papel, display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: 24, overflow: "hidden", flexShrink: 0 }}>
            {s.foto ? <img src={s.foto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : s.emoji}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{s.nombre}</div>
            <div style={{ fontSize: 11.5, color: COL.kraft, fontWeight: 600 }}>
              {`${cuantos(s.id)} ${cuantos(s.id) === 1 ? "artículo" : "artículos"}`}
            </div>
          </div>
          <button onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Subir"
            style={{ border: "none", background: "transparent", padding: 5,
              cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.25 : 1 }}>
            <ChevronUp size={21} color={COL.tinta} />
          </button>
          <button onClick={() => mover(i, 1)} disabled={i === secciones.length - 1} aria-label="Bajar"
            style={{ border: "none", background: "transparent", padding: 5,
              cursor: i === secciones.length - 1 ? "default" : "pointer",
              opacity: i === secciones.length - 1 ? 0.25 : 1 }}>
            <ChevronDown size={21} color={COL.tinta} />
          </button>
          <button onClick={() => setEdita({ ...s })} aria-label="Cambiar"
            style={{ border: "none", background: "transparent", padding: 5, cursor: "pointer" }}>
            <Pencil size={18} color={COL.kraft} />
          </button>
          <button onClick={() => setBorrar(s)} aria-label="Quitar"
            style={{ border: "none", background: "transparent", padding: 5, cursor: "pointer" }}>
            <Trash2 size={18} color={COL.chile} />
          </button>
        </div>
      ))}

      <button onClick={() => setEdita({ nueva: true, nombre: "", emoji: "📦", color: COLORES[0] })}
        style={{ width: "100%", marginTop: 6, padding: 14, borderRadius: 13, cursor: "pointer",
          fontFamily: "inherit", border: `2px dashed ${COL.kraft}`, background: "transparent",
          color: COL.tinta, fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8 }}>
        <Plus size={19} color={COL.chile} /> Agregar una sección
      </button>

      {borrar && <ModalBorrar sec={borrar} cuantos={cuantos(borrar.id)}
        otras={secciones.filter((x) => x.id !== borrar.id)}
        onCancel={() => setBorrar(null)} onOk={(dest) => quitar(borrar, dest)} />}
    </div>
  );
}

// ------------------------------------------------------------
// La ficha de una sección: nombre, figura, color, o foto propia.
// ------------------------------------------------------------
function FichaSeccion({ sec, onCancel, onOk }) {
  const [nombre, setNombre] = useState(sec.nombre || "");
  const [emoji, setEmoji] = useState(sec.emoji || "📦");
  const [color, setColor] = useState(sec.color || COLORES[0]);
  const [foto, setFoto] = useState(sec.foto || null);
  const [familia, setFamilia] = useState(FIGURAS[0].familia);
  const [tomando, setTomando] = useState(false);
  const listo = nombre.trim().length > 0;

  if (tomando) return <TomarFoto onCancel={() => setTomando(false)}
    onOk={(f) => { setFoto(f); setTomando(false); }} />;

  const grupo = FIGURAS.find((g) => g.familia === familia) || FIGURAS[0];

  return (
    <div style={{ padding: "12px 14px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button onClick={onCancel} aria-label="Cerrar la ficha"
          style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}>
          <X size={22} color={COL.kraft} />
        </button>
        <div style={{ fontSize: 19, fontWeight: 800, flex: 1 }}>
          {sec.nueva ? "Nueva sección" : "Cambiar la sección"}
        </div>
      </div>

      {/* Cómo va a quedar, en vivo */}
      <div style={{ background: COL.crema, borderRadius: 18, padding: "20px 14px", marginBottom: 14,
        borderLeft: `5px solid ${color}`, boxShadow: `0 2px 0 ${COL.sombra}` }}>
        <div style={{ fontSize: 38, lineHeight: 1 }}>
          {foto ? <img src={foto} alt="" style={{ width: 48, height: 48, borderRadius: 12, objectFit: "cover" }} />
            : emoji}
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6 }}>{nombre.trim() || "Sin nombre"}</div>
        <div style={{ fontSize: 12, color: COL.kraft, fontWeight: 600 }}>así se va a ver</div>
      </div>

      <label style={labelStyle}>¿Cómo se llama?</label>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus
        placeholder="Calcetines, Dulces, Herramienta..." style={{ ...inputStyle, fontSize: 17, fontWeight: 700 }} />

      <label style={{ ...labelStyle, display: "block", marginTop: 14 }}>Escoge la figura</label>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6, marginBottom: 9 }}>
        {FIGURAS.map((g) => (
          <button key={g.familia} onClick={() => setFamilia(g.familia)}
            style={{ padding: "7px 10px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
              fontSize: 12, fontWeight: 800,
              border: `2px solid ${familia === g.familia ? COL.billete : COL.kraft}`,
              background: familia === g.familia ? "#E4F0E8" : COL.papel,
              color: familia === g.familia ? COL.billete : COL.tinta }}>{g.familia}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))", gap: 6 }}>
        {grupo.lista.map((e) => (
          <button key={e} onClick={() => { setEmoji(e); setFoto(null); }}
            style={{ aspectRatio: "1 / 1", borderRadius: 12, cursor: "pointer", fontSize: 25,
              fontFamily: "inherit", background: !foto && emoji === e ? "#E4F0E8" : COL.papel,
              border: `2px solid ${!foto && emoji === e ? COL.billete : "transparent"}` }}>{e}</button>
        ))}
      </div>

      <button onClick={() => setTomando(true)}
        style={{ width: "100%", marginTop: 11, padding: 13, borderRadius: 12, cursor: "pointer",
          fontFamily: "inherit", border: `2px solid ${COL.kraft}`, background: "transparent",
          color: COL.tinta, fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8 }}>
        <Camera size={17} color={COL.billete} /> O ponle una foto tuya
      </button>
      {foto && (
        <button onClick={() => setFoto(null)}
          style={{ width: "100%", marginTop: 7, padding: 10, borderRadius: 11, cursor: "pointer",
            fontFamily: "inherit", border: "none", background: "transparent", color: COL.kraft,
            fontSize: 12, fontWeight: 800 }}>
          Quitar la foto y usar la figura
        </button>
      )}

      <label style={{ ...labelStyle, display: "block", marginTop: 14 }}>Color de la orilla</label>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 6 }}>
        {COLORES.map((c) => (
          <button key={c} onClick={() => setColor(c)} aria-label={`Color ${c}`}
            style={{ width: 38, height: 38, borderRadius: "50%", cursor: "pointer", background: c,
              border: color === c ? `3px solid ${COL.tinta}` : "3px solid transparent" }} />
        ))}
      </div>

      <button onClick={() => listo && onOk({ ...sec, nombre, emoji, color, foto })} disabled={!listo}
        style={{ width: "100%", marginTop: 18, padding: 16, fontSize: 17, fontWeight: 800,
          background: listo ? COL.billete : COL.kraft, color: "#fff", border: "none", borderRadius: 14,
          cursor: listo ? "pointer" : "default", fontFamily: "inherit", display: "flex",
          alignItems: "center", justifyContent: "center", gap: 8 }}>
        <Check size={20} /> {sec.nueva ? "Agregar la sección" : "Guardar la sección"}
      </button>
    </div>
  );
}

// ------------------------------------------------------------
// Borrar: si la sección tiene producto, se pregunta a dónde se va.
// Perder producto por borrar una sección sería imperdonable.
// ------------------------------------------------------------
function ModalBorrar({ sec, cuantos, otras, onCancel, onOk }) {
  const [dest, setDest] = useState(null);
  const puede = cuantos === 0 || !!dest;

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(32,20,10,0.5)", zIndex: 40,
      display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: COL.crema, width: "100%", maxWidth: 560,
        borderRadius: "24px 24px 0 0", padding: "20px 18px calc(20px + env(safe-area-inset-bottom))",
        maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: COL.kraft, borderRadius: 4, margin: "0 auto 16px", opacity: 0.5 }} />

        <div style={{ fontSize: 19, fontWeight: 800 }}>{`Quitar ${sec.nombre}`}</div>

        {cuantos === 0 ? (
          <div style={{ fontSize: 13, color: COL.kraft, fontWeight: 600, marginTop: 7, lineHeight: 1.4 }}>
            No tiene nada adentro, se puede quitar sin problema.
          </div>
        ) : (
          <>
            <div style={{ background: "#FFF3D6", borderRadius: 12, padding: "11px 12px", marginTop: 10,
              fontSize: 12.5, fontWeight: 700, color: COL.oro, lineHeight: 1.4 }}>
              {`Tiene ${cuantos} ${cuantos === 1 ? "artículo" : "artículos"}. No se van a borrar: dime a qué sección los paso.`}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 11 }}>
              {otras.map((o) => (
                <button key={o.id} onClick={() => setDest(o.id)}
                  style={{ padding: "10px 13px", borderRadius: 11, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 13.5, fontWeight: 700,
                    border: `2px solid ${dest === o.id ? COL.billete : COL.kraft}`,
                    background: dest === o.id ? "#E4F0E8" : COL.papel,
                    color: dest === o.id ? COL.billete : COL.tinta }}>{o.emoji} {o.nombre}</button>
              ))}
            </div>
          </>
        )}

        <button onClick={() => puede && onOk(dest)} disabled={!puede}
          style={{ width: "100%", marginTop: 16, padding: 15, fontSize: 16, fontWeight: 800,
            background: puede ? COL.chile : COL.kraft, color: "#fff", border: "none", borderRadius: 14,
            cursor: puede ? "pointer" : "default", fontFamily: "inherit" }}>
          {cuantos === 0 ? "Quitar la sección" : "Pasar el producto y quitar"}
        </button>
        <button onClick={onCancel}
          style={{ width: "100%", marginTop: 8, padding: 12, fontSize: 14, fontWeight: 800,
            background: "transparent", color: COL.kraft, border: "none", borderRadius: 12,
            cursor: "pointer", fontFamily: "inherit" }}>
          Mejor no
        </button>
      </div>
    </div>
  );
}

// Es servicio y no pestaña a propósito: se acomoda una vez y luego casi
// nunca. Entra desde la pantalla de venta, que es donde se está viendo.
export const moduloSecciones = {
  id: "secciones", nombre: "Secciones de la pantalla", version: "1.0", tipo: "servicio",
  icono: LayoutGrid, requiere: [],
  probar: () => {
    if (SECCIONES_BASE.length < 3) return "faltan las secciones de arranque";
    if (new Set(SECCIONES_BASE.map((s) => s.id)).size !== SECCIONES_BASE.length)
      return "hay dos secciones con el mismo id";
    const todas = FIGURAS.flatMap((g) => g.lista);
    if (todas.length < 50) return "el menú de figuras quedó muy corto";
    if (FIGURAS.some((g) => !g.familia || !g.lista.length)) return "hay una familia de figuras vacía";
    if (COLORES.length < 6) return "faltan colores para escoger";
    if (COLORES.some((c) => !/^#[0-9A-F]{6}$/i.test(c))) return "hay un color mal escrito";
    return true;
  },
};
